import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isNil } from 'lodash'

import Widget from '@/components/widget/Widget'

import { AiAnswersAnswerDrawer } from './AiAnswersAnswerDrawer'
import { AiAnswersKnowledgeGaps } from './AiAnswersKnowledgeGaps'
import { AiAnswersSearchLog } from './AiAnswersSearchLog'
import { AiAnswersSearchLogAction } from './AiAnswersSearchLogAction'
import { AiAnswersSources } from './AiAnswersSources'
import { fetchVerifiedAnswers, fetchPlaybookOptions } from './verifiedAnswers.api'
import { canEditAgentGpt } from './AiAnswers.helpers'

const NO_PLAYBOOKS = { options: [], loading: true, failed: false }

/**
 * @import import { AiAnswersVerifiedAnswersSection } from '@/views/AiAnswers/AiAnswersVerifiedAnswersSection'
 * @param {object} props
 * @param {object[]} props.rows The page's search log rows, listed in the search log and mined for
 *   knowledge gaps
 * @param {boolean} props.loading Whether the search log is fetching
 * @param {number} props.organizationId The org whose verified answers are read and written
 * @param {object} props.dateRange The page's date range, for the search log's CSV download
 */
export const AiAnswersVerifiedAnswersSection = ({ rows, loading, organizationId, dateRange }) => {
  const dispatch = useDispatch()
  const currentUser = useSelector((state) => state.currentUser)
  const [verifiedAnswers, setVerifiedAnswers] = useState(null)
  const [verifiedAnswersFailed, setVerifiedAnswersFailed] = useState(false)
  const [verifiedAnswersRequest, setVerifiedAnswersRequest] = useState(0)
  const [playbookOptions, setPlaybookOptions] = useState(NO_PLAYBOOKS)
  const [playbooksRequest, setPlaybooksRequest] = useState(0)
  const playbooks = {
    ...playbookOptions,
    retry: () => setPlaybooksRequest((request) => request + 1),
  }
  const canAnswer = canEditAgentGpt(currentUser)
  const verifiedAnswersLoading = isNil(verifiedAnswers) && !verifiedAnswersFailed

  useEffect(() => {
    let current = true
    setVerifiedAnswers(null)
    setVerifiedAnswersFailed(false)

    dispatch(fetchVerifiedAnswers({ organizationId }))
      .then((response) => {
        if (current) {
          setVerifiedAnswers(response)
        }
      })
      .catch(() => {
        if (current) {
          setVerifiedAnswersFailed(true)
        }
      })

    return () => {
      current = false
    }
  }, [organizationId, verifiedAnswersRequest])

  useEffect(() => {
    let current = true
    setPlaybookOptions(NO_PLAYBOOKS)

    dispatch(fetchPlaybookOptions({ organizationId }))
      .then((options) => {
        if (current) {
          setPlaybookOptions({ options, loading: false, failed: false })
        }
      })
      .catch(() => {
        if (current) {
          setPlaybookOptions({ options: [], loading: false, failed: true })
        }
      })

    return () => {
      current = false
    }
  }, [organizationId, playbooksRequest])

  // Every write refetches, so both sections show what agent-gpt now holds.
  const handleChange = () => {
    setVerifiedAnswersRequest((request) => request + 1)
  }

  // No column at all for users who can't author, so it doesn't sit there empty.
  const searchLogActions = canAnswer
    ? [
        {
          label: 'Edit answer',
          trigger: (row) => (
            <AiAnswersSearchLogAction
              row={row}
              verifiedAnswers={verifiedAnswers}
              playbooks={playbooks}
              organizationId={organizationId}
              onChange={handleChange}
            />
          ),
        },
      ]
    : []

  return (
    <>
      <Widget
        label="Knowledge gaps"
        subtitle="Questions AgentGPT couldn't answer"
        displayBackground={false}
      >
        <AiAnswersKnowledgeGaps
          rows={rows}
          loading={loading}
          organizationId={organizationId}
          verifiedAnswers={verifiedAnswers}
          verifiedAnswersLoading={verifiedAnswersLoading}
          playbooks={playbooks}
          canAnswer={canAnswer}
          onAnswered={handleChange}
        />
      </Widget>
      {/* Only AgentGPT Editors can act on what AgentGPT answers from, so nobody else sees it. */}
      {canAnswer && (
        <Widget
          label="Sources"
          subtitle="What AgentGPT answers from beyond your documents"
          displayBackground={false}
          customControls={
            <AiAnswersAnswerDrawer
              verifiedAnswers={verifiedAnswers}
              loading={verifiedAnswersLoading}
              playbooks={playbooks}
              organizationId={organizationId}
              onAnswered={handleChange}
            />
          }
        >
          <AiAnswersSources
            verifiedAnswers={verifiedAnswers}
            loading={verifiedAnswersLoading}
            playbooks={playbooks}
            canAnswer={canAnswer}
            organizationId={organizationId}
            onChange={handleChange}
          />
        </Widget>
      )}
      <AiAnswersSearchLog
        rows={rows}
        loading={loading}
        dateRange={dateRange}
        actions={searchLogActions}
      />
    </>
  )
}
